import React, { useEffect, useState } from "react";
import axios from "axios";
import StarIcon from "@mui/icons-material/Star";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Rating, Avatar } from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";

const TestimonialBanner = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setItemsPerPage(1);
      else if (window.innerWidth < 1024) setItemsPerPage(2);
      else setItemsPerPage(3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isPaused || !testimonials || testimonials.length <= itemsPerPage) return;
    const interval = setInterval(() => {
      handleNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex, isPaused, testimonials?.length, itemsPerPage]);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/testimonial/getapproved`);
      if (res.data?.success) {
        setTestimonials(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const handleNext = () => {
    if (!testimonials || testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % (testimonials.length - itemsPerPage + 1 || 1));
  };

  const handlePrev = () => {
    if (!testimonials || testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + (testimonials.length - itemsPerPage + 1 || 1)) % (testimonials.length - itemsPerPage + 1 || 1));
  };

  if (!testimonials || testimonials.length === 0) return null;

  return (
    <div className="w-full bg-[#f8fafc] py-12 px-4 md:px-8 mb-10 rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="max-w-7xl mx-auto relative">
        <div className="flex justify-between items-center mb-8 px-2">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800">
            What our clients say
          </h2>
          <div className="flex gap-2">
            <button 
              onClick={handlePrev}
              disabled={testimonials.length <= itemsPerPage}
              className="p-2 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-600"
              aria-label="Previous testimonial"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button 
              onClick={handleNext}
              disabled={testimonials.length <= itemsPerPage}
              className="p-2 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-600"
              aria-label="Next testimonial"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
        
        <div 
          className="relative overflow-hidden pt-10 -mt-10"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div 
            className="flex transition-transform duration-500 ease-in-out items-stretch"
            style={{ 
              transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)`,
            }}
          >
            {testimonials.map((item, index) => (
              <div
                key={`${item.testimonial_id}-${index}`}
                className="flex-shrink-0 w-full md:w-1/2 lg:w-1/3 px-3 h-auto self-stretch"
              >
                <div className="bg-white rounded-[2rem] p-8 flex flex-col h-full shadow-[0_15px_40px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)] hover:-translate-y-3 transition-all duration-300 relative border border-slate-50">
                  {/* Decorative Quote Icon */}
                  <div className="absolute top-8 right-8 text-slate-100/80 pointer-events-none">
                    <svg width="45" height="45" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V12C14.017 12.5523 13.5693 13 13.017 13H10.017V21H14.017ZM6.017 21L6.017 18C6.017 16.8954 6.9124 16 8.017 16H11.017C11.5693 16 12.017 15.5523 12.017 15V9C12.017 8.44772 11.5693 8 11.017 8H7.017C6.46472 8 6.017 8.44772 6.017 9V12C6.017 12.5523 5.5693 13 5.017 13H2.017V21H6.017Z" />
                    </svg>
                  </div>

                  {/* Header: User Profile */}
                  <div className="flex items-center gap-4 mb-6 relative">
                    <Avatar sx={{ width: 52, height: 52, bgcolor: '#a16207', fontSize: '1.2rem', fontWeight: 'bold', border: '3px solid white', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                      {item.user_name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h4 className="font-extrabold text-slate-900 text-base leading-tight">{item.user_name}</h4>
                      </div>
                      <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-0.5">Verified Customer</p>
                    </div>
                  </div>

                  {/* Rating Stars */}
                  <div className="flex gap-0.5 mb-5">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        sx={{
                          fontSize: 18,
                          color: i < item.ratings ? "#f59e0b" : "#e2e8f0",
                        }}
                      />
                    ))}
                  </div>

                  {/* Quote Body */}
                  <div className="flex-1 relative">
                    <p className="text-slate-600 text-sm md:text-base leading-relaxed font-medium italic">
                      "{item.comment}"
                    </p>
                    {item.comment.length > 120 && (
                      <button
                        onClick={() => setSelectedTestimonial(item)}
                        className="mt-2 text-yellow-700 text-xs font-bold hover:text-yellow-800 transition-colors cursor-pointer bg-transparent border-none p-0"
                      >
                        Read full experience →
                      </button>
                    )}
                  </div>

                  {/* Footer: Date & Meta */}
                  <div className="mt-8 pt-6 flex justify-between items-center text-[10px] text-slate-400 font-medium">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-full text-slate-500">
                      <VerifiedIcon sx={{ fontSize: 10, color: '#3b82f6' }} />
                      <span>Trusted Review</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots indicators */}
        {testimonials.length > itemsPerPage && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: testimonials.length - itemsPerPage + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentIndex === i ? 'bg-yellow-700 w-6' : 'bg-slate-300'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal for Read More */}
      <Dialog 
        open={!!selectedTestimonial} 
        onClose={() => setSelectedTestimonial(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: '1.5rem', p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>Testimonial</Typography>
          <Rating value={selectedTestimonial?.ratings || 0} readOnly size="small" />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#334155', fontStyle: 'italic', lineHeight: 1.6 }}>
            "{selectedTestimonial?.comment}"
          </Typography>
          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
            <Avatar sx={{ bgcolor: '#a16207' }}>{selectedTestimonial?.user_name?.charAt(0)}</Avatar>
            <div>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{selectedTestimonial?.user_name}</Typography>
              <Typography variant="caption" color="textSecondary">Verified Customer</Typography>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelectedTestimonial(null)} sx={{ color: '#a16207', fontWeight: 'bold' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TestimonialBanner;
